import { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { ChevronLeft, ChevronRight, Plus, Trash2, Lock, Sparkles, MapPin, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { logger } from "../lib/logger";

const HOUR_LABELS = Array.from({ length: 11 }, (_, i) => i + 8); // 08:00 → 18:00
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // 0 = Mon
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmtDateLabel(d) {
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function Availability() {
  const { user, refreshUser } = useAuth();
  const [slots, setSlots] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [busy, setBusy] = useState({});

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const load = async () => {
    try { const { data } = await api.get("/counsellor/slots"); setSlots(data); }
    catch (e) { toast.error("Could not load availability"); }
  };
  useEffect(() => { load(); }, []);

  const slotAt = (day, hour) => {
    return slots.find((s) => {
      const sd = new Date(s.start_at);
      return sameDay(sd, day) && sd.getHours() === hour;
    });
  };

  const isPast = (day, hour) => {
    const d = new Date(day);
    d.setHours(hour, 0, 0, 0);
    return d.getTime() < Date.now();
  };

  const addSlot = async (day, hour) => {
    const start = new Date(day);
    start.setHours(hour, 0, 0, 0);
    if (start.getTime() < Date.now()) return toast.error("That time has already passed");
    const key = `${day.toDateString()}-${hour}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.post("/counsellor/slots", { start_at: start.toISOString(), duration_minutes: 50 });
      toast.success("Slot added");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add"); }
    finally { setBusy((b) => ({ ...b, [key]: false })); }
  };

  const removeSlot = async (slotId, day, hour) => {
    const key = `${day.toDateString()}-${hour}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.delete(`/counsellor/slots/${slotId}`);
      toast.success("Slot removed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove"); }
    finally { setBusy((b) => ({ ...b, [key]: false })); }
  };

  const goWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(startOfWeek(d));
  };

  const totalOpen = slots.filter((s) => !s.is_booked && new Date(s.start_at) > new Date()).length;
  const totalBooked = slots.filter((s) => s.is_booked && new Date(s.start_at) > new Date()).length;

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="overline mb-2">Your availability</div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#3D405B] tracking-tight">When can clients book you?</h1>
            <p className="text-[#7A7D96] mt-2 max-w-xl">Tap an empty hour to open a 50-minute slot. Tap your own open slot to remove it. Booked slots are locked.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="brand-card px-4 py-2 text-sm" data-testid="availability-counts">
              <span className="font-bold text-primary">{totalOpen}</span> open · <span className="font-bold text-secondary">{totalBooked}</span> booked
            </div>
          </div>
        </div>

        {user?.role === "counsellor" && <OfficeDetailsPanel user={user} refreshUser={refreshUser} />}

        <div className="brand-card p-5 sm:p-6" data-testid="availability-calendar">
          <div className="flex items-center justify-between mb-5">
            <button onClick={() => goWeek(-1)} className="p-2 rounded-full hover:bg-[#3D405B]/5" data-testid="cal-prev"><ChevronLeft size={18} /></button>
            <div className="text-center">
              <div className="overline">Week of</div>
              <div className="font-extrabold text-[#3D405B]">{fmtDateLabel(days[0])} — {fmtDateLabel(days[6])}</div>
            </div>
            <button onClick={() => goWeek(1)} className="p-2 rounded-full hover:bg-[#3D405B]/5" data-testid="cal-next"><ChevronRight size={18} /></button>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-[60px_repeat(7,_1fr)] gap-1.5 mb-1.5">
                <div />
                {days.map((d, idx) => (
                  <div key={d.toISOString().slice(0, 10)} className="text-center text-xs">
                    <div className="font-bold uppercase tracking-[0.18em] text-[#7A7D96]">{DAY_LABELS[idx]}</div>
                    <div className="font-extrabold text-[#3D405B] text-base mt-0.5">{d.getDate()}</div>
                  </div>
                ))}
              </div>

              {HOUR_LABELS.map((hour) => (
                <div key={hour} className="grid grid-cols-[60px_repeat(7,_1fr)] gap-1.5 mb-1.5">
                  <div className="text-xs font-bold text-[#7A7D96] flex items-start justify-end pr-1 pt-1">{String(hour).padStart(2, "0")}:00</div>
                  {days.map((day) => {
                    const slot = slotAt(day, hour);
                    const past = isPast(day, hour);
                    const key = `${day.toDateString()}-${hour}`;
                    const isBusy = busy[key];
                    let content = null;
                    if (slot) {
                      if (slot.is_booked) {
                        content = (
                          <div className="h-full w-full rounded-xl bg-secondary text-white px-2 py-2 flex flex-col items-start justify-center cursor-not-allowed" data-testid={`cal-slot-booked-${slot.slot_id}`}>
                            <div className="text-[10px] uppercase tracking-wider font-bold opacity-80 flex items-center gap-1"><Lock size={10} /> Booked</div>
                            <div className="text-xs font-bold mt-0.5">50 min</div>
                          </div>
                        );
                      } else {
                        content = (
                          <button onClick={() => removeSlot(slot.slot_id, day, hour)} disabled={isBusy || past} className="h-full w-full rounded-xl bg-primary text-white px-2 py-2 flex flex-col items-start justify-center hover:bg-primary/90 transition-all disabled:opacity-60" data-testid={`cal-slot-open-${slot.slot_id}`}>
                            <div className="text-[10px] uppercase tracking-wider font-bold opacity-90">Open</div>
                            <div className="flex items-center gap-1 text-xs font-bold mt-0.5"><Trash2 size={11} /> Remove</div>
                          </button>
                        );
                      }
                    } else {
                      content = (
                        <button onClick={() => addSlot(day, hour)} disabled={isBusy || past} className={`h-full w-full rounded-xl border-2 border-dashed border-[#3D405B]/15 text-[#7A7D96] hover:border-primary hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center ${past ? "opacity-40 cursor-not-allowed hover:border-[#3D405B]/15 hover:text-[#7A7D96] hover:bg-transparent" : ""}`} data-testid={`cal-cell-${day.toISOString().slice(0,10)}-${hour}`} aria-label="Add slot">
                          <Plus size={14} />
                        </button>
                      );
                    }
                    return <div key={`${day.toISOString().slice(0,10)}-${hour}`} className="h-14">{content}</div>;
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3 text-xs text-[#7A7D96]">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-md bg-primary inline-block" /> Open</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-md bg-secondary inline-block" /> Booked</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-md border-2 border-dashed border-[#3D405B]/30 inline-block" /> Tap to add</span>
            <span className="flex items-center gap-1.5 text-secondary"><Sparkles size={12} /> Tip: clients see only open slots</span>
          </div>
        </div>
      </main>
    </div>
  );
}


function OfficeDetailsPanel({ user, refreshUser }) {
  const [address, setAddress] = useState(user?.office_address || "");
  const [mapUrl, setMapUrl] = useState(user?.office_map_url || "");
  const [saving, setSaving] = useState(false);

  // Sync when the user object refreshes (e.g. right after save)
  useEffect(() => {
    setAddress(user?.office_address || "");
    setMapUrl(user?.office_map_url || "");
  }, [user?.office_address, user?.office_map_url]);

  const dirty =
    (address.trim() || "") !== (user?.office_address || "") ||
    (mapUrl.trim() || "") !== (user?.office_map_url || "");

  const save = async (e) => {
    e?.preventDefault?.();
    if (saving || !dirty) return;
    if (mapUrl.trim() && !/^https?:\/\//i.test(mapUrl.trim())) {
      toast.error("Map link must start with http:// or https://");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/profile", {
        office_address: address.trim(),
        office_map_url: mapUrl.trim(),
      });
      await refreshUser?.();
      toast.success("Office details updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
      logger.warn("office update failed:", err?.message);
    } finally {
      setSaving(false);
    }
  };

  const hasAddress = !!(user?.office_address && user.office_address.trim());

  return (
    <div className="brand-card p-5 sm:p-6 mb-5" data-testid="office-details-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
        <div>
          <div className="overline mb-1.5">Office details</div>
          <h2 className="text-xl font-extrabold text-[#3D405B] tracking-tight">Where do you see clients in person?</h2>
          <p className="text-sm text-[#7A7D96] mt-1 max-w-2xl">
            {hasAddress
              ? "Clients see this address when they book an in-person session. Leave blank if you only see clients online."
              : "Add your office address to let clients book in-person sessions. Leave blank if you only see clients online."}
          </p>
        </div>
        {hasAddress && (
          <span className="pill bg-secondary/15 text-secondary self-start inline-flex items-center gap-1"><MapPin size={12} /> In-person enabled</span>
        )}
      </div>

      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="text-xs font-bold uppercase tracking-[0.16em] text-[#3D405B] mb-1.5 block">Office address</label>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="123 Main Rd, Rosebank, Johannesburg"
            className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none text-sm"
            data-testid="office-address-input"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-bold uppercase tracking-[0.16em] text-[#3D405B] mb-1.5 block">Google Maps link (optional)</label>
          <div className="relative">
            <input
              type="url"
              value={mapUrl}
              onChange={(e) => setMapUrl(e.target.value)}
              placeholder="https://maps.app.goo.gl/…"
              className="w-full px-4 py-3 pr-32 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none text-sm"
              data-testid="office-map-url-input"
            />
            {mapUrl.trim() && (
              <a
                href={mapUrl.trim()}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline px-2 py-1"
                data-testid="office-map-url-preview"
              >
                Preview <ExternalLink size={12} />
              </a>
            )}
          </div>
          <p className="text-xs text-[#7A7D96] mt-1.5">Helps clients find you. On Google Maps, tap Share → Copy link.</p>
        </div>
        <div className="sm:col-span-2 flex items-center justify-between gap-3">
          <p className="text-xs text-[#7A7D96]">
            {dirty ? "You have unsaved changes." : "Everything up to date."}
          </p>
          <button
            type="submit"
            disabled={!dirty || saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="office-save-btn"
          >
            <Save size={15} /> {saving ? "Saving…" : "Save office details"}
          </button>
        </div>
      </form>
    </div>
  );
}
